// Словарь подпроекта 6 «Финансы»: раздел «Финансы», оплаты, выписка банка, счета студии. Подключается в `ru.ts`.

const days = (prefix: string) => ({
  one: `${prefix} {count} день`,
  few: `${prefix} {count} дня`,
  many: `${prefix} {count} дней`,
  other: `${prefix} {count} дня`,
});

/** Раздел «Финансы» (ключи `finance.*`). */
export const ruFinance = {
  nav: 'Финансы',
  title: 'Финансы',
  lead: 'Деньги студии: остатки, счета клиентов и оплаты, расходы и выплаты команде, налоги и поток денег',
  hint: {
    title: 'Как сюда попадают деньги',
    text: 'Оплаты и расходы проще всего записывать вручную — с чеком или платёжкой. Если удобнее, загрузите выписку из интернет-банка: программа 1С для этого не нужна, файл выгружает любой банк. Платформа сама узнаёт счёт по номеру в назначении или по ИНН и сумме — остальное попадает в «Разобрать»',
    example: 'В назначении «Оплата по счёту СЧ-2026-0012» — оплата сразу ляжет на этот счёт',
  },
  period: {
    recent: 'За 90 дней',
    all: 'Всё время',
  },
  tabs: {
    overview: 'Обзор',
    invoices: 'Счета',
    payments: 'Оплаты',
    expenses: 'Расходы',
    payouts: 'Выплаты',
    drafts: 'Черновики счетов',
    taxes: 'Налоги',
    cashflow: 'Поток денег',
    projects: 'Проекты',
  },
  tiles: {
    cash: 'На счетах',
    cashHint: 'Начальный остаток каждого счёта плюс поступления и минус расходы и выплаты после его даты',
    receivable: 'Должны нам',
    receivableHint: 'Сколько осталось оплатить по всем выставленным счетам',
    overdue: 'Просрочено',
    overdueCount: {
      one: '{count} счёт',
      few: '{count} счёта',
      many: '{count} счетов',
      other: '{count} счёта',
    },
    nothingOverdue: 'Просрочек нет',
    pending: 'Разобрать выписку',
    pendingCount: {
      one: '{count} строка',
      few: '{count} строки',
      many: '{count} строк',
      other: '{count} строки',
    },
    pendingNone: 'Всё разобрано',
    open: 'Открыть',
    teamDebt: 'Должны команде',
    teamDebtHint: 'Подписанные акты исполнителей, по которым ещё не отмечена выплата',
    teamDebtNone: 'Все акты оплачены',
    tax: 'Налог к уплате',
    taxDue: 'до {date}',
    taxNone: 'Сейчас платить нечего',
    taxNoProfile: 'Налоговый профиль не заполнен',
    vat: 'Порог НДС',
    vatHint: 'Доход с начала года против порога, после которого студия становится плательщиком НДС',
    vatShare: '{share}% порога',
    vatPayer: 'Студия платит НДС',
    drafts: 'Черновики счетов',
    draftsNone: 'Новых черновиков нет',
  },
  accounts: {
    title: 'Счета студии',
    hint: 'Где лежат деньги: расчётные счета и касса. Счёт из реквизитов студии добавляется сам; начальный остаток — сколько было на дату начала учёта',
    bankDefault: 'Расчётный счёт',
    add: 'Добавить счёт',
    edit: 'Изменить счёт',
    kind: 'Вид',
    kindBank: 'Банк',
    kindCash: 'Касса',
    name: 'Название',
    namePlaceholder: 'Т-Банк, основной',
    number: 'Номер счёта',
    bik: 'БИК',
    opening: 'Начальный остаток',
    openingOn: 'На дату',
    openingHint:
      'Сколько было на счёте в этот день. Поступления после этой даты прибавятся к остатку, раньше — нет',
    archived: 'В архиве',
    archive: 'В архив',
    restore: 'Вернуть из архива',
    save: 'Сохранить',
    saved: 'Счёт сохранён',
    since: 'с {date}',
    empty: 'Счетов пока нет',
    emptyText: 'Заполните расчётный счёт в реквизитах студии — он появится здесь сам, или добавьте кассу',
  },
  invoices: {
    filterOpen: 'Неоплаченные',
    filterOverdue: 'Просроченные',
    filterPaid: 'Оплаченные',
    filterAll: 'Все',
    columns: {
      invoice: 'Счёт',
      client: 'Клиент',
      amount: 'Сумма',
      paid: 'Оплачено',
      due: 'Срок',
      status: 'Оплата',
    },
    status: {
      unpaid: 'Не оплачен',
      partial: 'Частично',
      paid: 'Оплачен',
      overpaid: 'Переплата',
      void: 'Отозван',
    },
    overdue: days('Просрочен на'),
    markPaid: 'Отметить оплату',
    emptyTitle: 'Счетов нет',
    emptyText: 'Счета выставляются из сделки — на вкладке «Договор»',
    emptyFilteredTitle: 'Таких счетов нет',
    emptyFilteredText: 'Под этот фильтр ничего не попало',
    showAll: 'Показать все',
    dueTitle: 'Пора выставить',
    dueHint:
      'Доли графика оплат из заявки, чьё время пришло: подписан договор, начался этап или работа сдана, — а счёта ещё нет. Выставьте счёт в сделке или во вкладке «Документы» проекта',
    dueOpen: 'К сделке',
  },
  payments: {
    add: 'Добавить оплату',
    import: 'Загрузить выписку',
    importHint:
      'Файл выписки из интернет-банка (.txt «для 1С / 1CClientBankExchange»). Сама программа 1С не нужна — такой файл выгружает любой банк. Без выписки всё можно вносить вручную',
    columns: {
      date: 'Дата',
      amount: 'Сумма',
      from: 'От кого',
      account: 'Счёт',
      allocation: 'Куда легла',
    },
    advance: 'Аванс {amount}',
    allocated: '{number} · {amount}',
    other: 'Прочее поступление',
    notTaxable: 'Не доход',
    fromStatement: 'Из выписки',
    manual: 'Вручную',
    receipt: 'Чек',
    receiptAttach: 'Прикрепить чек',
    receiptOpen: 'Открыть чек',
    receiptReplace: 'Заменить чек',
    receiptRemove: 'Открепить чек',
    receiptAttached: 'Чек прикреплён',
    receiptRemoved: 'Чек откреплён',
    receiptMissing: 'Без чека',
    reallocate: 'Разнести',
    remove: 'Удалить',
    removed: 'Оплата удалена',
    actions: 'Действия с оплатой',
    emptyTitle: 'Оплат за выбранный период нет',
    emptyText: 'Добавьте оплату вручную или загрузите выписку банка',
  },
  dialog: {
    title: 'Оплата',
    lead: 'Поступление на счёт студии. Оплата клиента ложится на его неоплаченные счета — выбранный первым, остальные от старых к новым; остаток станет авансом',
    account: 'Счёт студии',
    date: 'Дата поступления',
    amount: 'Сумма',
    kind: 'Откуда',
    kindClient: 'От клиента',
    kindOther: 'Прочее',
    client: 'Клиент',
    clientPlaceholder: 'Найдите клиента',
    invoice: 'Счёт',
    invoiceAuto: 'Сначала старые счета',
    invoiceOption: '{number} · осталось {left}',
    payer: 'Плательщик',
    purpose: 'Назначение',
    taxable: 'Это доход для налога',
    taxableHint: 'Займ, возврат от поставщика или перевод со своего счёта — не доход: снимите отметку',
    save: 'Записать оплату',
    saved: 'Оплата записана',
    noAccounts: 'Сначала добавьте счёт студии на вкладке «Обзор»',
    receipt: 'Чек или платёжка',
    receiptHint: 'PDF или фото до 20 МБ — подтверждение оплаты останется у записи',
    uploading: 'Загружаем файл…',
  },
  reallocate: {
    title: 'Разнести оплату',
    lead: 'Оплата ляжет заново: выбранный счёт первым, остальное — на старые счета клиента, остаток — аванс',
    save: 'Разнести',
    done: 'Оплата разнесена',
  },
  statement: {
    reading: 'Загружаем выписку…',
    importedTitle: 'Выписка загружена',
    importedText: 'Строк {total}: разнесено само {matched}, в разбор {pending}, уже были {duplicates}',
    wrongFile: 'Нужен текстовый файл выписки из интернет-банка (.txt, формат «для 1С»)',
  },
  pending: {
    title: 'Разобрать',
    lead: 'Строки выписки, которые платформа не узнала сама. Поступление — оплата клиента или прочее; списание — расход по категории или перевод между своими счетами',
    incoming: 'Поступление',
    outgoing: 'Списание',
    outgoingHint: 'Запишите списание расходом или переводом на свой счёт',
    suggestionCategory: 'Похоже на «{category}»',
    suggestionTransfer: 'Похоже на перевод на свой счёт',
    asExpense: 'Расход',
    asTransfer: 'Перевод',
    expenseTitle: 'Списание как расход',
    transferTitle: 'Перевод между счетами',
    toAccount: 'На счёт',
    resolvedExpense: 'Расход записан',
    resolvedTransfer: 'Перевод записан',
    suggestionInvoice: 'Похоже на {number} · {client}',
    suggestionClient: 'Похоже на клиента {client}',
    accept: 'Принять',
    asClient: 'Оплата клиента',
    asOther: 'Прочее',
    skip: 'Пропустить',
    skipped: 'Строка пропущена',
    resolved: 'Оплата записана',
    pickClient: 'Чья это оплата',
    pickOutgoing: 'Что это за списание',
    fromAccount: 'Со счёта',
  },
  contract: {
    paid: 'Оплачен',
    partial: 'Оплачено {paid} из {amount}',
    partialShort: 'Оплачен частично',
    unpaid: 'Не оплачен · срок {date}',
    overdue: days('Просрочен на'),
  },
  settings: {
    title: 'Оплата счетов',
    lead: 'Срок оплаты выставленных счетов: после него счёт «Просрочен», ответственный получает напоминание на 1-й и 7-й день',
    dueDays: 'Срок оплаты, рабочих дней',
    dueDaysHint: 'Считается от даты счёта по производственному календарю студии',
    saved: 'Сохранено',
    taxTitle: 'Налоги и НДС',
    taxLead: 'Напоминания администраторам о сроке налога и пороге НДС',
    taxRemindDays: 'Напомнить о налоге за, дней',
    taxRemindDaysHint: 'И ещё раз — в день срока, если налог не уплачен',
    vatWarn: 'Предупредить о НДС при, % порога',
    vatThresholds: 'Порог НДС по годам, ₽',
    vatThresholdsHint: 'Пороги 2026–2028 заполнены по закону; поменяйте, если закон изменится',
    vatYear: 'Год',
    vatAddYear: 'Добавить год',
  },
  categories: {
    rent: 'Аренда',
    salary: 'Зарплата',
    software: 'Сервисы и софт',
    equipment: 'Техника',
    marketing: 'Маркетинг',
    services: 'Подрядчики и услуги',
    taxes: 'Налоги',
    other: 'Прочее',
    payout: 'Выплата по акту',
    refund: 'Возврат клиенту',
    owner_withdrawal: 'Вывод на личный счёт',
  },
  expenses: {
    add: 'Добавить расход',
    transfer: 'Перевод между счетами',
    filterAll: 'Все категории',
    filterProject: 'Все проекты',
    columns: {
      date: 'Дата',
      amount: 'Сумма',
      category: 'Категория',
      to: 'Кому',
      project: 'Проект',
      account: 'Счёт',
      receipt: 'Чек',
    },
    remove: 'Удалить',
    removed: 'Расход удалён',
    edit: 'Изменить',
    actions: 'Действия с расходом',
    emptyTitle: 'Расходов пока нет',
    emptyText: 'Запишите расход вручную и прикрепите чек — или загрузите выписку банка',
    transfersTitle: 'Переводы между счетами',
    transferRow: '{from} → {to}',
    transferRemoved: 'Перевод удалён',
    fromStatement: 'Из выписки',
    taxPeriod: 'За {quarter} кв. {year}',
  },
  expenseDialog: {
    title: 'Расход',
    editTitle: 'Изменить расход',
    lead: 'Списание со счёта студии. Расход проекта уменьшит его маржу',
    account: 'Со счёта',
    date: 'Дата',
    amount: 'Сумма',
    category: 'Категория',
    counterparty: 'Кому',
    counterpartyInn: 'ИНН получателя',
    purpose: 'За что',
    project: 'Проект',
    projectNone: 'Не относится к проекту',
    client: 'Клиент',
    refundInvoice: 'По какому счёту возврат',
    refundInvoiceNone: 'Не по счёту',
    refundInvoiceHint: 'Возврат по счёту уменьшает оплаченное: вернули всё — счёт снова ждёт оплаты',
    refundInvoiceOption: '№ {number} · оплачено {paid}',
    taxPeriod: 'За какой квартал налог',
    quarter: '{quarter} квартал {year}',
    save: 'Записать расход',
    saved: 'Расход записан',
    payoutHint: 'Выплаты исполнителям записываются на вкладке «Выплаты» — по подписанному акту',
    ownerWithdrawalHint:
      'Деньги со счёта ИП себе: видны в движении денег, но в налог, себестоимость проектов и накладные не входят',
    taxPeriodAuto: 'Понять по назначению платежа',
    receiptReplace: 'Новый файл заменит прикреплённый чек',
  },
  transferDialog: {
    title: 'Перевод между счетами',
    lead: 'Деньги переходят со счёта на счёт студии — это не доход и не расход',
    from: 'Со счёта',
    to: 'На счёт',
    date: 'Дата',
    amount: 'Сумма',
    comment: 'Комментарий',
    save: 'Записать перевод',
    saved: 'Перевод записан',
  },
  payouts: {
    lead: 'Подписанные акты исполнителей и выплаты по ним. Отметьте выплату и прикрепите чек — он появится и у акта в документах',
    debtTitle: 'Должны команде',
    debtNone: 'Долгов перед командой нет',
    debtHint: 'Остаток по актам исполнителей плюс подтверждённые часы, которые ещё не вошли в акт',
    personTotal: 'Должны {amount}',
    unbilled: 'Часы без акта: {amount}',
    onSigning: 'На подписи',
    columns: {
      act: 'Акт',
      employee: 'Исполнитель',
      date: 'Дата акта',
      amount: 'По акту',
      paid: 'Выплачено',
      left: 'Осталось',
      status: 'Статус',
    },
    status: {
      unpaid: 'Не выплачен',
      partial: 'Частично',
      paid: 'Выплачен',
    },
    filterOpen: 'К выплате',
    filterAll: 'Все акты',
    pay: 'Отметить выплату',
    emptyTitle: 'Актов к выплате нет',
    emptyText: 'Здесь появятся подписанные акты исполнителей',
    dialogTitle: 'Выплата по акту {number}',
    dialogLead: 'Исполнитель {employee}: по акту {amount}, осталось {left}',
    save: 'Записать выплату',
    saved: 'Выплата записана',
    receiptRequired: 'Прикрепите чек или платёжку — он подтверждает выплату',
  },
  drafts: {
    lead: 'Первого числа платформа готовит черновики счетов: почасовые проекты — за прошлый месяц по списанным часам, подписки — тариф текущего месяца авансом, часы сверх пакета и прямые расходы прошлого. Проверьте строки и выставьте',
    tariffLine: 'Поддержка по тарифу «{tariff}» за {month}',
    support: 'Поддержка',
    overLine: 'Часы сверх пакета · {hours}',
    expenseLine: 'Прямые расходы: {what}',
    prepareMonthHint:
      'Подписка выставляется авансом — за текущий месяц, с часами сверх пакета и прямыми расходами прошлого; почасовой проект — за прошедший месяц',
    invoiceLabel: '{project} · {month}',
    prepare: 'Подготовить черновик',
    prepareTitle: 'Черновик счёта за месяц',
    prepareMonth: 'Месяц',
    project: 'Проект',
    prepared: 'Черновик готов',
    nothingToBill: 'За этот месяц выставлять нечего',
    issue: 'Выставить счёт',
    issued: 'Счёт выставлен',
    dismiss: 'Не выставлять',
    dismissed: 'Черновик отклонён',
    restore: 'Вернуть',
    reason: 'Причина',
    save: 'Сохранить',
    saved: 'Черновик сохранён',
    total: 'Итого {amount}',
    needsDeal: 'Нет сделки с подписанной заявкой — счёт выставить нельзя',
    filterNew: 'Новые',
    filterAll: 'Все',
    state: {
      draft: 'Новый',
      issued: 'Выставлен',
      dismissed: 'Отклонён',
    },
    emptyTitle: 'Черновиков нет',
    emptyText: 'Они появятся первого числа для почасовых проектов и подписок',
    lineEnabled: 'Включить в счёт',
    openInvoice: 'Открыть счёт',
  },
  taxes: {
    lead: 'Налог по УСН нарастающим итогом: доходы — оплаты с отметкой «доход для налога», уплата — расходы категории «Налоги» за квартал',
    year: 'Год',
    noProfile: 'Налоговый профиль студии не заполнен — заполните его в настройках цен',
    regime: {
      usn_income: 'УСН «Доходы», {rate}%',
      usn_income_minus_expenses: 'УСН «Доходы минус расходы», {rate}%',
    },
    columns: {
      quarter: 'Период',
      income: 'Доход',
      expense: 'Расход',
      base: 'База',
      accrued: 'Начислено',
      paid: 'Уплачено',
      left: 'К уплате',
      due: 'Срок',
    },
    quarter: '{quarter} кв.',
    year4: 'Год',
    pay: 'Отметить уплату',
    next: 'Следующий платёж: {amount} до {date}',
    paidHint: 'Уплата записывается расходом «Налоги» с кварталом — так она попадёт в зачёт',
    vatTitle: 'Порог НДС {year}',
    vatText:
      'Доход {income} из {threshold} ({share}%). С учётом ожидаемых оплат — {forecast} ({forecastShare}%)',
    vatOk: 'До порога далеко',
    vatWarn: 'Близко к порогу — обсудите с бухгалтером',
    vatExceeded: 'Порог превышен — со следующего месяца студия платит НДС',
    vatPayer: 'Студия уже платит НДС — порог не отслеживается',
    vatLastYear:
      'Доход {year} года выше порога НДС, а в налоговом профиле на 1 января НДС нет — студия уже платит НДС: обновите налоговый профиль',
    vatNextMonth: 'НДС — с 1-го числа следующего месяца: обновите налоговый профиль',
    openProfile: 'Налоговый профиль',
  },
  projectMoney: {
    tab: 'Деньги',
    lead: 'Маржа проекта: план — по заявке и смете выбранного пакета, факт — оплаты минус часы команды, накладные, прямые расходы и налог, ожидается — факт плюс неоплаченные и ещё не выставленные счета',
    planNote: 'выручка {revenue} · {pct}',
    planRevenueOnly: 'Выручка по заявке — снимка сметы нет',
    factNote: 'оплачено {revenue} − себестоимость {cost} − налог {tax} · {pct}',
    expectedNote: 'выручка {revenue} · {pct}',
    noInvoices: 'Счетов по проекту пока нет',
    invoiceDates: 'от {issued} · срок {due}',
    expenses: 'Прямые расходы',
    noExpenses: 'Расходов, привязанных к проекту, нет',
  },
  today: {
    title: 'Деньги',
    hint: 'Что требует внимания в финансах: просроченные счета, строки выписки без пары, налог к сроку, черновики счетов и порог НДС',
    overdue: 'Просрочено {amount}',
    pending: 'Разобрать выписку',
    tax: 'Налог {amount} — до {date}',
    drafts: 'Черновики счетов',
    due: 'Пора выставить счёт',
    vat: 'Порог НДС',
    open: 'В финансы',
  },
  cashflow: {
    lead: 'Деньги по месяцам: прошлые — факт, будущие — прогноз по срокам неоплаченных счетов, ФОТ и накладным из настроек цен',
    months: 'Месяцев вперёд',
    columns: {
      month: 'Месяц',
      inflow: 'Поступления',
      outflow: 'Списания',
      net: 'Итог',
      balance: 'Остаток',
    },
    fact: 'Факт',
    forecast: 'Прогноз',
    negative: 'В {month} остаток уходит в минус — проверьте поступления',
    allPositive: 'Остаток не уходит в минус',
    overhead: 'Накладные по плану',
  },
  margins: {
    lead: 'Маржа проектов: выручка — выставленные счета, себестоимость — часы команды по ставкам и прямые расходы проекта',
    columns: {
      project: 'Проект',
      revenue: 'Выручка',
      paid: 'Оплачено',
      cost: 'Себестоимость',
      margin: 'Маржа',
      marginPct: 'Маржа, %',
    },
    plan: 'План',
    fact: 'Факт',
    expected: 'Ожидается',
    emptyTitle: 'Проектов пока нет',
    emptyText: 'Маржа появится, когда по проекту будут счета или списанные часы',
    costBreakdown: 'Часы команды {hours} · накладные {overhead} · прямые расходы {direct} · налог {tax}',
    planBreakdown: 'Выручка {revenue} − себестоимость по смете {cost} − налог {tax}',
    planMargin: 'маржа {margin} · {pct}',
  },
} as const;

/** Ошибки (в `errors.finance`). */
export const ruFinanceErrors = {
  account_number: 'Номер расчётного счёта — 20 цифр',
  account_exists: 'Такой счёт уже есть',
  client_required: 'Выберите клиента',
  invoice_foreign: 'Этот счёт выставлен другому клиенту или отозван',
  statement_bad_format:
    'Файл не похож на выписку банка (формат «1CClientBankExchange»): строка {line}. Выгрузите выписку в интернет-банке как «для 1С» или внесите оплаты вручную',
  statement_foreign_account:
    'Выписка по счёту {account} — такого счёта студии нет. Добавьте его в «Счета студии» на вкладке «Обзор»',
  statement_too_large: 'Выписка слишком большая — выгрузите период покороче',
  payout_act_required: 'Выберите подписанный акт исполнителя',
  payout_exceeds_act: 'По акту осталось выплатить {left}',
  transfer_same_account: 'Счета «откуда» и «куда» должны быть разными',
  draft_needs_deal: 'У проекта нет сделки с подписанной заявкой — счёт выставить нельзя',
  draft_empty: 'В черновике нет включённых строк',
  draft_already_issued: 'Черновик уже выставлен или отклонён',
} as const;

/** «История» и журнал (в `audit`). */
export const ruFinanceAudit = {
  actions: {
    finance_payment: {
      recorded: 'Записана оплата',
      removed: 'Оплата удалена',
      restored: 'Оплата возвращена',
      allocated: 'Оплата разнесена заново',
      receipt_attached: 'Прикреплён чек',
      receipt_removed: 'Чек откреплён',
    },
    finance_statement: {
      imported: 'Загружена выписка банка',
    },
    finance_account: {
      created: 'Добавлен счёт студии',
      changed: 'Изменён счёт студии',
    },
    finance_settings: {
      changed: 'Изменены настройки финансов',
    },
    finance_expense: {
      recorded: 'Записан расход',
      changed: 'Изменён расход',
      removed: 'Расход удалён',
      restored: 'Расход возвращён',
      receipt_attached: 'Прикреплён чек',
      receipt_removed: 'Чек откреплён',
    },
    finance_transfer: {
      recorded: 'Записан перевод между счетами',
      removed: 'Перевод удалён',
      restored: 'Перевод возвращён',
    },
    finance_invoice_draft: {
      issued: 'Счёт выставлен из черновика',
      changed: 'Изменён черновик счёта',
      dismissed: 'Черновик счёта отклонён',
      restored: 'Черновик счёта возвращён',
    },
  },
  entities: {
    finance_payment: 'Оплата',
    finance_statement: 'Выписка банка',
    finance_account: 'Счёт студии',
    finance_settings: 'Настройки финансов',
    finance_expense: 'Расход',
    finance_transfer: 'Перевод между счетами',
    finance_invoice_draft: 'Черновик счёта',
  },
  fields: {
    paidOn: 'Дата оплаты',
    account: 'Счёт',
    invoices: 'Счета',
    file: 'Файл',
    total: 'Строк',
    duplicates: 'Уже были',
    matched: 'Разнесено само',
    pending: 'В разбор',
    openingBalance: 'Начальный остаток',
    openingOn: 'Остаток на дату',
    invoiceDueDays: 'Срок оплаты счёта',
    spentOn: 'Дата расхода',
    from: 'Со счёта',
    to: 'На счёт',
    on: 'Дата',
    month: 'Месяц',
  },
} as const;

/** Типы уведомлений (в `notifications`). */
export const ruFinanceNotificationTypes = {
  typeFinanceInvoicePaid: 'Счёт оплачен',
  typeFinanceInvoicePaidHint: 'Клиент оплатил счёт по вашей сделке — по выписке или отметке в «Финансах»',
  typeFinanceInvoiceOverdue: 'Счёт просрочен',
  typeFinanceInvoiceOverdueHint: 'Утром на 1-й и 7-й день просрочки счёта по вашей сделке или клиенту',
  typeFinanceInvoiceDraft: 'Черновик счёта готов',
  typeFinanceInvoiceDraftHint:
    'Первого числа — черновики счетов за прошлый месяц по почасовым проектам и подпискам',
  typeFinancePayout: 'Выплата по акту',
  typeFinancePayoutHint: 'Студия отметила выплату по вашему акту',
  typeFinanceTaxDue: 'Срок налога',
  typeFinanceTaxDueHint: 'Администраторам: за несколько дней до срока уплаты налога и в сам день',
  typeFinanceVat: 'Порог НДС',
  typeFinanceVatHint: 'Администраторам: доход года подошёл к порогу НДС или превысил его',
} as const;

/** Тексты уведомлений (в `finance.notifications`). */
export const ruFinanceNotifications = {
  paidTitle: 'Оплачен счёт {number} · {amount}',
  overdueTitle: 'Счёт {number}: {overdue}',
  overdueDays: days('просрочен на'),
  overdueBody: '{client} · осталось {left}',
  payoutTitle: 'Выплата по акту {number} · {amount}',
  payoutBody: 'Чек — у акта в документах',
  draftTitle: 'Черновик счёта за {month}',
  taxTitle: 'Налог за {quarter} кв. {year}: к уплате {amount}',
  taxBody: 'Срок — {date}',
  taxBodyToday: 'Срок — сегодня, {date}',
  vatWarnTitle: 'Доход года — {share}% порога НДС',
  vatExceededTitle: 'Порог НДС превышен ({share}%)',
  vatBody: 'Доход {income} при пороге {threshold}',
} as const;
